'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Portal } from '@chakra-ui/react';
import { WalletConnector, WalletInstitution, WalletInstitutionConnection } from '@/modules/Wallet/interface/walet';
import { useConnectInstitution } from '@/modules/Wallet/http/client/mutations/useConnectInstitution';
import { useDisconnectInstitution } from '@/modules/Wallet/http/client/mutations/useDisconnectInstitution';
import { useUpdateInstitution } from '@/modules/Wallet/http/client/mutations/useUpdateInstitution';
import { useGetPluggyToken } from '@/modules/Wallet/http/client/useGetPluggyToken';
import { useAuth } from '@/contexts/AuthContext';
import { ModalCentered } from '@/components/atoms/ModalCentered';
const PluggyConnect = dynamic(() => import('react-pluggy-connect').then(mod => mod.PluggyConnect), {
  ssr: false
});
export const OpenFinanceWidget = () => {
  const {
    t
  } = useTranslation('common');
  const {
    user
  } = useAuth();
  const [connectorToRemove, setConnectorToRemove] = useState<Partial<WalletInstitutionConnection> & {
    item_id?: string;
  }>({});
  const [connectorToUpdate, setConnectorToUpdate] = useState<Partial<WalletInstitution> & {
    item_id?: string;
  }>({});
  const [pluggyStateData, setPluggyStateData] = useState({
    connectingPluggy: false,
    idPluggyToConnect: undefined as number | undefined,
    connectorsIds: [] as number[]
  });
  const {
    data: pluggyToken = {
      access_token: ''
    },
    isFetching: isFetchingPluggyToken
  } = useGetPluggyToken(connectorToUpdate?.item_id && user?.id ? {
    client_user_id: user.id,
    item_id: connectorToUpdate.item_id
  } : undefined);
  const {
    mutate: removeItem
  } = useDisconnectInstitution();
  const {
    mutate: registerPluggyConnector
  } = useConnectInstitution();
  const {
    mutate: updatePluggyConnector
  } = useUpdateInstitution();
  const isLoading = isFetchingPluggyToken || !pluggyToken?.access_token;
  const setIsConnecting = (connectingPluggy: boolean) => setPluggyStateData(prevState => ({
    ...prevState,
    connectingPluggy
  }));
  const setSelectedId = (idPluggyToConnect?: number) => setPluggyStateData(prevState => ({
    ...prevState,
    idPluggyToConnect
  }));
  const setConnectorIds = (connectorsIds: number[]) => setPluggyStateData(prevState => ({
    ...prevState,
    connectorsIds
  }));
  const openOpenFinanceDialog = async (event: Event) => {
    const customEvent = event as CustomEvent<{
      connector: WalletConnector;
    }>;
    const {
      connector
    } = customEvent.detail;
    if (!connector?.id) {
      return;
    }
    setIsConnecting(true);
    setConnectorIds([Number(connector?.id)]);
    setSelectedId(Number(connector?.id));
  };
  const handleSuccess = async (itemData: {
    item: {
      id: string;
    };
  }) => {
    if (connectorToUpdate?.item_id) {
      updatePluggyConnector(connectorToUpdate?.item_id);
      handleClose();
      return;
    }
    registerPluggyConnector(itemData?.item.id);
    handleClose();
  };
  const handleClose = () => setIsConnecting(false);
  const removeSelectedConnector = (event: Event) => {
    const customEvent = event as CustomEvent<{
      connector: WalletInstitutionConnection;
    }>;
    const {
      connector
    } = customEvent.detail;
    setConnectorToRemove(connector);
  };
  const uploadItemConnector = (event: Event) => {
    const customEvent = event as CustomEvent<{
      connector: WalletInstitution;
    }>;
    const {
      connector
    } = customEvent.detail;
    setIsConnecting(true);
    setConnectorToUpdate(connector);
  };
  const confirmConnectorRemoval = async () => {
    const itemToDelete = connectorToRemove?.item_id;
    removeItem(String(itemToDelete));
    setConnectorToRemove({});
  };
  const resetStates = () => {
    setConnectorToRemove({});
    setConnectorToUpdate({});
    setPluggyStateData({
      connectingPluggy: false,
      idPluggyToConnect: undefined,
      connectorsIds: []
    });
  };
  useEffect(() => {
    window.addEventListener(`loaded-pluggy-widget`, openOpenFinanceDialog);
    window.addEventListener(`remove-connected-broker`, removeSelectedConnector);
    window.addEventListener(`upload-item-pluggy-widget`, uploadItemConnector);
    return () => {
      window.removeEventListener(`loaded-pluggy-widget`, openOpenFinanceDialog);
      window.removeEventListener(`remove-connected-broker`, removeSelectedConnector);
      window.removeEventListener(`upload-item-pluggy-widget`, uploadItemConnector);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    return () => {
      resetStates();
    };
  }, []);
  useEffect(() => {
    if (!pluggyStateData.connectingPluggy) {
      resetStates();
    }
  }, [pluggyStateData.connectingPluggy]);
  if (connectorToRemove?.item_id) {
    return <Portal>
        <ModalCentered title={t('screens.myWallet.drawers.modalConfirmRemoveBroker.title')} description={t('screens.myWallet.drawers.modalConfirmRemoveBroker.description')} buttonConfirmText={t('screens.myWallet.drawers.modalConfirmRemoveBroker.confirm')} onConfirm={confirmConnectorRemoval} onCancel={() => {
        setConnectorToRemove({});
      }} />
      </Portal>;
  }
  if (!pluggyStateData?.connectingPluggy || isLoading) return <></>;
  return <PluggyConnect connectToken={pluggyToken.access_token} includeSandbox={false} {...pluggyStateData.connectorsIds?.length > 0 && {
    connectorIds: pluggyStateData.connectorsIds
  }} {...!!pluggyStateData.idPluggyToConnect && {
    selectedConnectorId: pluggyStateData.idPluggyToConnect
  }} {...connectorToUpdate?.item_id && {
    updateItem: connectorToUpdate?.item_id
  }} onSuccess={handleSuccess} onClose={handleClose} data-sentry-element="PluggyConnect" data-sentry-component="OpenFinanceWidget" data-sentry-source-file="index.tsx" />;
};